import Storage from '../../../core/Storage';
import Orientation from '../../../core/Orientation';
import Audio from '../../../core/Audio';
import Utils from '../../../core/Utils';
import Pevents from '../../../core/Pevents';
import { TutorialStep } from '../../../types/alice-bob';
import { setTutorialStep } from '../../backend/store/tutorial';
import { fetchMe } from '../../backend/store';
import * as Sentry from "@sentry/browser";

export default class extends Phaser.Scene {

    private loading_text: Phaser.GameObjects.Text;
    private timer: Phaser.Time.TimerEvent;
    private failedAssets: Record<string, number> = {};

    constructor() {
        super('Boot');
    }

    init() {
        window.devtodev.customEvent("game_play_started", { "stage": 4 })
        const loading_text = this.add.text(this.cameras.main.centerX, this.cameras.main.centerY, 'Please wait, loading', { fontSize: 32 })
        loading_text.setOrigin(0.5, 0.5)

        this.loading_text = this.add.text(this.cameras.main.centerX, this.cameras.main.centerY + 50, '...', { fontSize: 32 })
        this.loading_text.setOrigin(0.5, 0.5)

        this.timer = this.time.addEvent({
            delay: 1000,
            loop: true,
            callback: () => {
                this.loading_text.setText(this.loading_text.text + '.')
            },
            callbackScope: this
        })

        this.events.on('shutdown', () => {
            console.log('Boot Scene shutting down...');
            this.timer.destroy();
        });
    }

    preload() {
        window.devtodev.customEvent("game_play_started", { "stage": 5 })

        this.load.on('loaderror', this.onLoadError, this);

        this.load.image('preload_logo', 'assets/images/preload/logo.webp');
        this.load.image('preload_bar', 'assets/images/preload/bar.png');

        this.load.json('i18n', `./assets/i18n/${this.registry.get('playerLocale')}.json`);

        window.Telegram.WebApp.setHeaderColor('#000');
    }

    onLoadError(file: Phaser.Loader.File) {
        const maxRetries = 3;
        if (!this.failedAssets[file.key]) this.failedAssets[file.key] = 0;

        window.devtodev.customEvent("asset_load_error", { "asset": file.key, "attempt": this.failedAssets[file.key] + 1 });

        if (this.failedAssets[file.key] < maxRetries) {
            this.failedAssets[file.key]++;
            console.warn(`[Retry] Loading failed for: ${file.src}. Retrying... (Attempt ${this.failedAssets[file.key]})`);
            Sentry.captureMessage(`[Retry] Loading failed for: ${file.src}. Retrying... (Attempt ${this.failedAssets[file.key]})`);
            this.load.addFile(file);
        } else {
            console.error(`[Fatal] Failed to load ${file.src} after ${maxRetries} retries.`);
            Sentry.captureMessage(`[Fatal] Failed to load ${file.src} after ${maxRetries} retries.`);
        }
    }

    async create() {
        window.devtodev.customEvent("game_play_started", { "stage": 6 })

        if (this.game.scale.scaleMode === Phaser.Scale.ScaleModes.NONE) {
            this.scale.resize(this.cameras.main.width, this.cameras.main.height);
        } else {
            this.scale.setGameSize(this.cameras.main.width, this.cameras.main.height);
        }

        // init p33 modules
        this.game.storage = new Storage();
        this.game.orientation = new Orientation();
        this.game.audio = new Audio(this);
        this.game.utils = new Utils(this);
        this.game.pevents = new Pevents();

        try {
            await fetchMe();
        } catch (error) {
            window.devtodev.customEvent("profile_fetch_failed", { "error": String(error) });
            this.showRetryScreen();
            return;
        }

        window.devtodev.customEvent("game_play_started", { "stage": 7 })
        this.scene.start('Preload');
    }

    showRetryScreen() {
        this.timer.destroy();
        this.children.removeAll(true);

        const errorText = this.add.text(
            this.cameras.main.centerX,
            this.cameras.main.centerY - 50,
            'Connection failed.\nPlease check your internet\nand try again.',
            { fontSize: '28px', align: 'center' }
        );
        errorText.setOrigin(0.5, 0.5);

        const retryButton = this.add.text(
            this.cameras.main.centerX,
            this.cameras.main.centerY + 80,
            '[ Retry ]',
            { fontSize: '36px', color: '#4CAF50' }
        );
        retryButton.setOrigin(0.5, 0.5);
        retryButton.setInteractive({ useHandCursor: true });

        retryButton.on('pointerdown', async () => {
            retryButton.disableInteractive();
            retryButton.setText('Loading...');
            window.devtodev.customEvent("profile_fetch_retry", {});

            try {
                await fetchMe();
                window.devtodev.customEvent("game_play_started", { "stage": 7 })
                this.scene.start('Preload');
            } catch (error) {
                retryButton.setText('[ Retry ]');
                retryButton.setInteractive({ useHandCursor: true });
            }
        });
    }
}
